import type { OpportunityPnl, OpportunityPnlMonth, OpportunityPnlYearPlan } from '../types';

/** All monetary line values for one computed month (raw inputs + derived subtotals). */
export type PnlMoneyValues = {
  revenue: number;
  otherIncome: number;
  totalOutput: number;
  cogs: number;
  grossProfit: number;
  personnel: number;
  rentPremises: number;
  marketing: number;
  researchDevelopment: number;
  vehicleTravel: number;
  insuranceContributions: number;
  repairsMaintenance: number;
  generalAdmin: number;
  otherOpex: number;
  totalOpex: number;
  ebitda: number;
  depreciation: number;
  ebit: number;
  interestExpense: number;
  preTaxResult: number;
  taxes: number;
  netIncome: number;
  cumulativeEbitda: number;
};

/** Ratio values in percent (null when the base is zero / not applicable). */
export type PnlPercentValues = {
  grossMarginPct: number | null;
  ebitdaMarginPct: number | null;
  netMarginPct: number | null;
  revenueGrowthPct: number | null;
};

export type PnlComputedMonth = PnlMoneyValues & PnlPercentValues & { month: number };

export type PnlComputedYear = {
  year: number;
  months: PnlComputedMonth[];
  /** Year totals; percent fields are computed from annual sums, revenueGrowthPct is YoY. */
  totals: PnlComputedMonth;
};

export type PnlMoneyKey = keyof PnlMoneyValues;
export type PnlPercentKey = keyof PnlPercentValues;

export type PnlRowDef = {
  key: PnlMoneyKey | PnlPercentKey;
  label: string;
  kind: 'money' | 'percent';
  emphasis?: 'subtotal' | 'total';
  indent?: boolean;
  /** Cost lines are rendered as negative amounts (deductions). */
  expense?: boolean;
};

export type PnlSectionDef = {
  key: string;
  /** Optional section divider label rendered above the rows. */
  title?: string;
  rows: PnlRowDef[];
};

/** Full BWA-style row layout: income → gross profit → cost blocks → results → ratios & cash view. */
export const PNL_SECTIONS: PnlSectionDef[] = [
  {
    key: 'output',
    rows: [
      { key: 'revenue', label: 'Revenue (Umsatzerlöse)', kind: 'money' },
      { key: 'otherIncome', label: 'Other operating income', kind: 'money', indent: true },
      { key: 'totalOutput', label: 'Total output (Gesamtleistung)', kind: 'money', emphasis: 'subtotal' },
      { key: 'cogs', label: 'Cost of materials / COGS', kind: 'money', indent: true, expense: true },
      { key: 'grossProfit', label: 'Gross profit (Rohertrag)', kind: 'money', emphasis: 'subtotal' },
    ],
  },
  {
    key: 'opex',
    title: 'Operating costs',
    rows: [
      { key: 'personnel', label: 'Personnel & salaries', kind: 'money', indent: true, expense: true },
      { key: 'rentPremises', label: 'Rent & premises', kind: 'money', indent: true, expense: true },
      { key: 'marketing', label: 'Marketing & sales', kind: 'money', indent: true, expense: true },
      { key: 'researchDevelopment', label: 'Research & development', kind: 'money', indent: true, expense: true },
      { key: 'vehicleTravel', label: 'Vehicle & travel', kind: 'money', indent: true, expense: true },
      { key: 'insuranceContributions', label: 'Insurance & contributions', kind: 'money', indent: true, expense: true },
      { key: 'repairsMaintenance', label: 'Repairs & maintenance', kind: 'money', indent: true, expense: true },
      { key: 'generalAdmin', label: 'General & administrative', kind: 'money', indent: true, expense: true },
      { key: 'otherOpex', label: 'Other operating expenses', kind: 'money', indent: true, expense: true },
      { key: 'totalOpex', label: 'Total operating costs', kind: 'money', emphasis: 'subtotal', expense: true },
    ],
  },
  {
    key: 'results',
    title: 'Results',
    rows: [
      { key: 'ebitda', label: 'EBITDA (operating result)', kind: 'money', emphasis: 'subtotal' },
      { key: 'depreciation', label: 'Depreciation & amortization', kind: 'money', indent: true, expense: true },
      { key: 'ebit', label: 'EBIT (Betriebsergebnis)', kind: 'money', emphasis: 'subtotal' },
      { key: 'interestExpense', label: 'Interest expense', kind: 'money', indent: true, expense: true },
      { key: 'preTaxResult', label: 'Result before taxes', kind: 'money', emphasis: 'subtotal' },
      { key: 'taxes', label: 'Income taxes', kind: 'money', indent: true, expense: true },
      { key: 'netIncome', label: 'Net income (vorläufiges Ergebnis)', kind: 'money', emphasis: 'total' },
    ],
  },
  {
    key: 'ratios',
    title: 'Ratios & cash view',
    rows: [
      { key: 'grossMarginPct', label: 'Gross margin (% of output)', kind: 'percent', indent: true },
      { key: 'ebitdaMarginPct', label: 'EBITDA margin (% of output)', kind: 'percent', indent: true },
      { key: 'netMarginPct', label: 'Net margin (% of output)', kind: 'percent', indent: true },
      { key: 'revenueGrowthPct', label: 'Revenue growth (MoM · year total = YoY)', kind: 'percent', indent: true },
      { key: 'cumulativeEbitda', label: 'Cumulative EBITDA (cash position)', kind: 'money', emphasis: 'total' },
    ],
  },
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function monthLabel(month: number): string {
  if (month >= 1 && month <= 12) return MONTH_LABELS[month - 1]!;
  return `M${month}`;
}

function pct(numerator: number, denominator: number): number | null {
  if (denominator === 0) return null;
  return (numerator / denominator) * 100;
}

function computeMoney(raw: OpportunityPnlMonth): PnlMoneyValues {
  const otherIncome = raw.otherIncome ?? 0;
  const rentPremises = raw.rentPremises ?? 0;
  const vehicleTravel = raw.vehicleTravel ?? 0;
  const insuranceContributions = raw.insuranceContributions ?? 0;
  const repairsMaintenance = raw.repairsMaintenance ?? 0;

  const totalOutput = raw.revenue + otherIncome;
  const grossProfit = totalOutput - raw.cogs;
  const totalOpex =
    raw.personnel +
    rentPremises +
    raw.marketing +
    raw.researchDevelopment +
    vehicleTravel +
    insuranceContributions +
    repairsMaintenance +
    raw.generalAdmin +
    raw.otherOpex;
  const ebitda = grossProfit - totalOpex;
  const ebit = ebitda - raw.depreciation;
  const preTaxResult = ebit - raw.interestExpense;
  const netIncome = preTaxResult - raw.taxes;

  return {
    revenue: raw.revenue,
    otherIncome,
    totalOutput,
    cogs: raw.cogs,
    grossProfit,
    personnel: raw.personnel,
    rentPremises,
    marketing: raw.marketing,
    researchDevelopment: raw.researchDevelopment,
    vehicleTravel,
    insuranceContributions,
    repairsMaintenance,
    generalAdmin: raw.generalAdmin,
    otherOpex: raw.otherOpex,
    totalOpex,
    ebitda,
    depreciation: raw.depreciation,
    ebit,
    interestExpense: raw.interestExpense,
    preTaxResult,
    taxes: raw.taxes,
    netIncome,
    cumulativeEbitda: 0,
  };
}

const MONEY_SUM_KEYS: PnlMoneyKey[] = [
  'revenue',
  'otherIncome',
  'totalOutput',
  'cogs',
  'grossProfit',
  'personnel',
  'rentPremises',
  'marketing',
  'researchDevelopment',
  'vehicleTravel',
  'insuranceContributions',
  'repairsMaintenance',
  'generalAdmin',
  'otherOpex',
  'totalOpex',
  'ebitda',
  'depreciation',
  'ebit',
  'interestExpense',
  'preTaxResult',
  'taxes',
  'netIncome',
];

/**
 * Computes all plan years at once so month-over-month growth and the cumulative
 * EBITDA cash view carry over correctly across year boundaries.
 */
export function computePnlYears(plans: OpportunityPnlYearPlan[]): PnlComputedYear[] {
  const sortedPlans = [...plans].sort((a, b) => a.year - b.year);
  const years: PnlComputedYear[] = [];

  let cumulativeEbitda = 0;
  let previousMonthRevenue: number | null = null;
  let previousYearRevenue: number | null = null;

  for (const plan of sortedPlans) {
    const rawMonths = [...plan.months].sort((a, b) => a.month - b.month);
    const months: PnlComputedMonth[] = [];

    for (const raw of rawMonths) {
      const money = computeMoney(raw);
      cumulativeEbitda += money.ebitda;

      const revenueGrowthPct =
        previousMonthRevenue !== null && previousMonthRevenue !== 0
          ? ((money.revenue - previousMonthRevenue) / Math.abs(previousMonthRevenue)) * 100
          : null;
      previousMonthRevenue = money.revenue;

      months.push({
        ...money,
        month: raw.month,
        cumulativeEbitda,
        grossMarginPct: pct(money.grossProfit, money.totalOutput),
        ebitdaMarginPct: pct(money.ebitda, money.totalOutput),
        netMarginPct: pct(money.netIncome, money.totalOutput),
        revenueGrowthPct,
      });
    }

    const totals = months.reduce<Record<PnlMoneyKey, number>>(
      (acc, month) => {
        for (const key of MONEY_SUM_KEYS) acc[key] += month[key];
        return acc;
      },
      Object.fromEntries(MONEY_SUM_KEYS.map((key) => [key, 0])) as Record<PnlMoneyKey, number>
    );

    const yearRevenueGrowthPct =
      previousYearRevenue !== null && previousYearRevenue !== 0
        ? ((totals.revenue - previousYearRevenue) / Math.abs(previousYearRevenue)) * 100
        : null;
    previousYearRevenue = totals.revenue;

    years.push({
      year: plan.year,
      months,
      totals: {
        ...totals,
        month: 0,
        cumulativeEbitda,
        grossMarginPct: pct(totals.grossProfit, totals.totalOutput),
        ebitdaMarginPct: pct(totals.ebitda, totals.totalOutput),
        netMarginPct: pct(totals.netIncome, totals.totalOutput),
        revenueGrowthPct: yearRevenueGrowthPct,
      },
    });
  }

  return years;
}

export function pnlYearPlans(pnl: OpportunityPnl): OpportunityPnlYearPlan[] {
  if (pnl.yearPlans?.length) {
    return [...pnl.yearPlans].sort((a, b) => a.year - b.year);
  }
  return [];
}
